import { ChevronRightIcon } from '@heroicons/react/24/solid'
import clsx from 'clsx'
import PropTypes from 'prop-types'
import { useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import Skeleton from 'react-loading-skeleton'
import { useLocation } from 'react-router'

import { useSampleDataDialog } from '../../context/sample-data-context'
import {
  SAMPLE_DATA_AVAILABILITY,
  isSampleTemplatePartner
} from '../../helpers/sample-data'
import {
  useSampleDataAccess,
  useSampleDataSummary
} from '../../hooks/use-sample-data'
import Divider from '../divider'
import Button from '../tailwind/Button'
import SettingsContainer from './settings-container'

const P = 'sample_data.'

// Feste Adresse für CS und API-Doku: /settings#sample-data leitet auf
// /settings/dashboard#sample-data weiter und scrollt hierher.
const ANCHOR = 'sample-data'

const SettingsRow = ({ title, hint, action, onClick }) => {
  const interactive = !!onClick

  return (
    <button
      type='button'
      onClick={onClick}
      disabled={!interactive}
      className={clsx(
        'group flex w-full items-center justify-between gap-x-4 rounded-md text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-600 focus-visible:ring-offset-2 focus-visible:ring-offset-gray-50',
        interactive ? 'cursor-pointer' : 'pointer-events-none opacity-60'
      )}
    >
      <div className='min-w-0'>
        <p className='text-sm text-gray-900'>{title}</p>
        {hint && <p className='text-sm text-gray-500'>{hint}</p>}
      </div>
      <div className='flex flex-shrink-0 items-center gap-x-2'>
        {interactive && action && (
          <span className='text-sm text-gray-500 group-hover:text-gray-700'>
            {action}
          </span>
        )}
        <ChevronRightIcon
          className={clsx(
            'h-5 w-5 flex-none text-gray-300 group-hover:text-gray-700',
            !interactive && 'opacity-0'
          )}
          aria-hidden='true'
        />
      </div>
    </button>
  )
}

SettingsRow.propTypes = {
  title: PropTypes.string.isRequired,
  hint: PropTypes.string,
  action: PropTypes.string,
  onClick: PropTypes.func
}

SettingsRow.defaultProps = {
  hint: undefined,
  action: undefined,
  onClick: undefined
}

/**
 * Beispieldaten unter /settings/dashboard (Anker #sample-data). Zählt beim
 * Öffnen frisch beim Backend. Eine Zeile, die den Dialog öffnet: Entfernen,
 * wenn welche da sind, sonst Hinzufügen. Ohne Löschrecht oder im
 * Discover-Onboarding öffnet die Zeile den Dialog im Info-Modus, ohne
 * Beispieldaten und ohne Hinzufüge-Recht ist sie gesperrt.
 */
const SampleDataSettings = ({ className }) => {
  const { t } = useTranslation()
  const { hash } = useLocation()
  const { openSampleDataDialog } = useSampleDataDialog()
  const { gate, canAdd, isTemplatePartner, partnerId } = useSampleDataAccess()
  const summary = useSampleDataSummary({ enabled: !isTemplatePartner })

  const containerRef = useRef(null)

  useEffect(() => {
    if (hash !== `#${ANCHOR}`) return
    containerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }, [hash])

  const data = summary.data
  const hasSampleData = !!data && !data.blocked && !data.isEmpty

  const renderRow = () => {
    if (summary.isPending) {
      return <Skeleton width={240} height={20} />
    }

    // Das Backend verweigert die Zählung wegen der Rolle. Ein neuer Versuch
    // ändert daran nichts, also Hinweis statt Fehler.
    if (data?.blocked?.reason === 'NO_PERMISSION') {
      return (
        <p className='text-sm text-gray-500'>
          {t(P + 'settings.no_permission_hint')}
        </p>
      )
    }

    if (summary.isError || !data || data.blocked) {
      return (
        <div className='flex flex-wrap items-center gap-x-3 gap-y-1'>
          <p className='text-sm text-gray-500'>
            {t(P + 'remove.summary_error')}
          </p>
          <Button.Text
            text={t(P + 'remove.retry')}
            onClick={() => summary.refetch()}
            minWidth='min-w-0'
          />
        </div>
      )
    }

    if (hasSampleData) {
      const counts = [
        data.careers.length > 0 &&
          t(P + 'remove.careers', { count: data.careers.length }),
        data.talents.total > 0 &&
          t(P + 'remove.talents', { count: data.talents.total })
      ].filter(Boolean)

      // Ohne Löschrecht oder im Onboarding nur ansehen, Dialog im Info-Modus.
      // Dort kein "Entfernen", der Chevron zeigt, dass die Zeile klickbar ist.
      const { NO_PERMISSION, ONBOARDING } = SAMPLE_DATA_AVAILABILITY
      const canOpenInfo = gate === NO_PERMISSION || gate === ONBOARDING

      return (
        <SettingsRow
          title={counts.join(' · ')}
          action={
            gate === null ? t(P + 'settings.row_present_action') : undefined
          }
          onClick={
            gate === null
              ? () =>
                  openSampleDataDialog({ mode: 'remove', source: 'settings' })
              : canOpenInfo
                ? () =>
                    openSampleDataDialog({ mode: 'info', source: 'settings' })
                : undefined
          }
        />
      )
    }

    return (
      <SettingsRow
        title={t(P + 'settings.row_none')}
        hint={t(P + 'settings.row_none_hint')}
        action={t(P + 'settings.row_none_action')}
        onClick={
          canAdd
            ? () => openSampleDataDialog({ mode: 'add', source: 'settings' })
            : undefined
        }
      />
    )
  }

  const topHint =
    gate === SAMPLE_DATA_AVAILABILITY.NO_PERMISSION
      ? t(P + 'settings.no_permission_hint')
      : gate === SAMPLE_DATA_AVAILABILITY.ONBOARDING && hasSampleData
        ? t(P + 'info.onboarding')
        : null

  return (
    <div
      ref={containerRef}
      id={ANCHOR}
      className={clsx('scroll-mt-6', className)}
    >
      <SettingsContainer
        title={t(P + 'settings.title')}
        description={t(P + 'settings.description')}
      >
        {isTemplatePartner ? (
          <p className='text-sm text-gray-500'>
            {t(
              P +
                (isSampleTemplatePartner(partnerId)
                  ? 'info.template'
                  : 'info.protected')
            )}
          </p>
        ) : (
          <>
            {topHint && <p className='text-sm text-gray-500'>{topHint}</p>}
            <div>
              <h4 className='text-sm font-medium leading-6 text-gray-900'>
                {t(P + 'settings.group_title')}
              </h4>
              <p className='text-sm text-gray-500'>
                {t(P + 'settings.group_hint')}
              </p>
              <div className='mt-4 flex flex-col gap-y-4'>
                <Divider marginY={0} />
                {renderRow()}
              </div>
            </div>
          </>
        )}
      </SettingsContainer>
    </div>
  )
}

SampleDataSettings.propTypes = {
  className: PropTypes.string
}

SampleDataSettings.defaultProps = {
  className: undefined
}

export default SampleDataSettings
